import {
  useState, useEffect, useMemo,
} from 'react';
import {
  Form, Input, Toggle,
} from '@skyline-platform/alchemy';
import PropTypes from 'prop-types';
import '../FullScreenForm.css';
import { validateStringLength } from '@utils/formValidators';
import * as is from 'is';
import {
  getZones,
  saveZone,
  updateZone,
} from '@utils/graphql/creators/zones';
import { useDebouncedCallback } from 'use-debounce';
import { useFormSubmit } from '@utils/react/hooks/useFormSubmit';
import { FormFooter } from '../FormFooter/FormFooter';
import { getLogger } from '@utils/general';
import { parseBoolean } from '../helpers';
const logger = getLogger('src.components.ZoneForm');

export const ZoneForm = ({
  zoneData,
  handleClose,
  setNewCreatedZone,
}) => {
  const [name, setName] = useState(zoneData?.name || '');
  const [description, setDescription] = useState(zoneData?.description || '');
  const [duplicateName, setDuplicateName] = useState(false);
  const [autoOfflineEnabled, setAutoOfflineEnabled] = useState(parseBoolean(zoneData?.auto_offline_enabled));

  // Derive error and allFieldsValid using useMemo
  const error = useMemo(() => {
    const nameValid = is.empty(name)
      ? { isValid: false, error: null }
      : duplicateName
        ? { isValid: false, error: 'Zone name already exists. Please update the zone name.' }
        // Stryker disable next-line StringLiteral
        : validateStringLength(name, 1, 32, 'Name');
    // Stryker disable next-line StringLiteral
    const descValid = validateStringLength(description, 0, 140, 'Description');
    return {
      name: {
        isValid: nameValid.isValid,
        error: nameValid.error,
      },
      description: {
        isValid: descValid.isValid,
        error: descValid.error,
      },
    };
  }, [
    name,
    description,
    duplicateName,
  ]);

  const allFieldsValid = useMemo(() => (
    error.name.isValid && error.description.isValid
  ), [error]);

  const checkDuplicateZoneName = async (name) => {
    const { query, execute } = getZones(null, { name });
    query.addReturnProperties('id', 'name');

    await execute()
      .then(({ error, data }) => {
        if (error) {
          logger.error(error);
        }
        if (data && data.length > 0) {
          setDuplicateName(true);
        } else {
          setDuplicateName(false);
        }
      })
      .catch(() => {
        logger.error(
          `There was a failure retrieving data for zone: ${name}`
        );
      });
  };

  const debouncedValidateZoneName = useDebouncedCallback(checkDuplicateZoneName, 300);
  useEffect(() => {
    if (name.length === 0 || zoneData?.name === name) {
      setTimeout(() => setDuplicateName(false), 0);
    } else {
      debouncedValidateZoneName(name, setDuplicateName);
    }
  }, [name]);

  const submitZone = useFormSubmit({
    saveCreator: (payload) => saveZone('create_zone', payload),
    updateCreator: (id, payload) => updateZone('update_zone', id, payload),
    existingId: zoneData?.id,
    addReturnProperties: (mutation) => mutation.addReturnProperties('id', 'name'),
    logMessages: { success: `Successfully created zone ${name}.`, fail: `Failed to create zone ${name}.` },
    updateLogMessages: { success: `Successfully updated zone ${name}.`, fail: 'Failed to edit zone.' },
    onSuccess: (data) => {
      if (typeof setNewCreatedZone === 'function') setNewCreatedZone(data);
    },
    handleClose,
  });

  const handleZone = () => submitZone({
    name,
    description,
    auto_offline_enabled: !!autoOfflineEnabled,
  });

  return (
    <gar-zone-form>
      <Form
        formTitle='Create New Zone'
        footerPlacement='right'
        footer={[
          <FormFooter
            key='footer'
            onCancel={handleClose}
            onSubmit={handleZone}
            submitLabel={zoneData ? 'Update Zone' : 'Create New Zone'}
            isValid={allFieldsValid}
          />,
        ]}
      >
        <Input
          id='zone-name'
          inputLabel='Name'
          helperText='Zone Name'
          onChange={(e) => { setName(e.target.value); }}
          value={name}
          type='text'
          required
          // Stryker disable next-line StringLiteral
          errorMessage={error.name.isValid ? '' : error.name.error}
        />
        <Input
          id='zone-description'
          inputLabel='Description'
          helperText='Description of the Zone'
          onChange={(e) => { setDescription(e.target.value); }}
          value={description}
          type='text'
          multiline
          rows={6}
          // Stryker disable next-line StringLiteral
          errorMessage={error.description.isValid ? '' : error.description.error}
        />
        <Toggle
          label='Transcode Auto-Offline'
          checked={autoOfflineEnabled}
          handleChange={(e) => setAutoOfflineEnabled(e.target.checked)}
        />
      </Form>
    </gar-zone-form>
  );
};

ZoneForm.propTypes = {
  zoneData: PropTypes.object,
  handleClose: PropTypes.func,
  setFormData: PropTypes.func,
  setNewCreatedZone: PropTypes.func,
};